import { create } from "zustand";
import { getApiUrl } from "@/utils/apiConfig";

const API_URL = getApiUrl();

interface SettingsStore {
  settings: Record<string, string>;
  isLoaded: boolean;
  fetchSettings: () => Promise<void>;
  updateSetting: (key: string, value: string | boolean) => Promise<void>;
  getSetting: (key: string, fallback: string | boolean) => string | boolean;
}

export const useSettingsStore = create<SettingsStore>((set, get) => ({
  settings: {},
  isLoaded: false,

  fetchSettings: async () => {
    try {
      const res = await fetch(`${API_URL}/settings.php?t=${Date.now()}`, {
        headers: { 'Cache-Control': 'no-cache' }
      });
      if (!res.ok) throw new Error("API Error");
      
      const data = await res.json();
      
      if (data && typeof data === 'object' && !data.error) {
        set({ settings: data, isLoaded: true });

        // Dispatch legacy events for backward compatibility during transition
        Object.entries(data).forEach(([key, value]) => {
          if (key === "kaynin_soft_launch_enabled") {
              window.dispatchEvent(new CustomEvent("softLaunchModeChanged", { detail: { enabled: value === "true" } }));
              window.dispatchEvent(new Event("softLaunchChanged"));
          } else if (key === "kaynin_demo_mode_enabled") {
              window.dispatchEvent(new Event("demoModeChanged"));
          } else if (key === "kaynin_staging_mode_enabled") {
              window.dispatchEvent(new CustomEvent("stagingModeChanged", { detail: { enabled: value === "true" } }));
          } else if (key === "kaynin_commercial_mode_enabled") {
              window.dispatchEvent(new CustomEvent("commercialModeChanged", { detail: { enabled: value === "true" } }));
          } else if (key === "kaynin_chatbot_enabled") {
              window.dispatchEvent(new StorageEvent("storage", { key }));
          } else if (key === "kaynin_barometer_enabled") {
              window.dispatchEvent(new CustomEvent("barometerEnabledChanged", { detail: { enabled: value === "true" } }));
          } else if (key === "kaynin_barometer_mode") {
              window.dispatchEvent(new CustomEvent("barometerModeChanged", { detail: { mode: value } }));
          } else if (key === "kaynin_strict_accreditation_mode") {
              window.dispatchEvent(new Event("accreditationsSettingsChanged"));
          } else if (key === "kaynin_barometer_config") {
              try {
                  window.dispatchEvent(new CustomEvent("barometerConfigChanged", { detail: JSON.parse(value as string) }));
              } catch (e) {}
          } else if (key === "kaynin_posters_config") {
              window.dispatchEvent(new CustomEvent("postersConfigChanged"));
          } else if (key === "kayninMediatheque") {
              window.dispatchEvent(new CustomEvent("mediathequeChanged"));
          }
        });
      }
    } catch (e) {
      console.error("Failed to fetch global settings", e);
    }
  },

  updateSetting: async (key: string, value: string | boolean) => {
    const stringValue = typeof value === "boolean" ? (value ? "true" : "false") : value;
    
    try {
      const token = localStorage.getItem('kaynin_auth_token');
      const res = await fetch(`${API_URL}/settings.php`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify({ action: 'update_single', key, value: stringValue })
      });

      if (!res.ok) {
        const errText = await res.text();
        throw new Error(`Erreur serveur: ${res.status} - ${errText}`);
      }

      // Update UI only after success
      set((state) => ({
        settings: { ...state.settings, [key]: stringValue }
      }));

      // Dispatch legacy events
      if (key === "kaynin_soft_launch_enabled") {
          window.dispatchEvent(new CustomEvent("softLaunchModeChanged", { detail: { enabled: value === true || value === "true" } }));
          window.dispatchEvent(new Event("softLaunchChanged"));
      } else if (key === "kaynin_demo_mode_enabled") {
          window.dispatchEvent(new Event("demoModeChanged"));
      } else if (key === "kaynin_staging_mode_enabled") {
          window.dispatchEvent(new CustomEvent("stagingModeChanged", { detail: { enabled: value === true || value === "true" } }));
      } else if (key === "kaynin_commercial_mode_enabled") {
          window.dispatchEvent(new CustomEvent("commercialModeChanged", { detail: { enabled: value === true || value === "true" } }));
      } else if (key === "kaynin_chatbot_enabled") {
          window.dispatchEvent(new StorageEvent("storage", { key }));
      } else if (key === "kaynin_barometer_enabled") {
          window.dispatchEvent(new CustomEvent("barometerEnabledChanged", { detail: { enabled: value === true || value === "true" } }));
      } else if (key === "kaynin_barometer_mode") {
          window.dispatchEvent(new CustomEvent("barometerModeChanged", { detail: { mode: value } }));
      } else if (key === "kaynin_strict_accreditation_mode") {
          window.dispatchEvent(new Event("accreditationsSettingsChanged"));
      } else if (key === "kaynin_barometer_config") {
          try {
              window.dispatchEvent(new CustomEvent("barometerConfigChanged", { detail: typeof value === "string" ? JSON.parse(value) : value }));
          } catch (e) {}
      } else if (key === "kaynin_posters_config") {
          window.dispatchEvent(new CustomEvent("postersConfigChanged"));
      } else if (key === "kayninMediatheque") {
          window.dispatchEvent(new CustomEvent("mediathequeChanged"));
      }
    } catch (e) {
      console.error("Failed to update setting", e);
      throw e;
    }
  },

  getSetting: (key: string, fallback: string | boolean) => {
    const val = get().settings[key];
    if (val !== undefined) {
      if (typeof fallback === "boolean") {
        return val === "true";
      }
      return val;
    }
    return fallback;
  }
}));

// Initialize settings on load
if (typeof window !== "undefined") {
  useSettingsStore.getState().fetchSettings();
}
